import cn from 'classnames';
import { type FC, useRef } from 'react';
import {
    trackPromoClick,
    trackPromoImpressions,
    buildPromoTrackingObject,
} from '../helpers/promoTrackingHelpers';
import { useHomePageContext } from '../HomePageContext';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { TRADE } from 'dibs-buyer-layout/exports/userTypeEnums';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';

// components
import { Link } from 'dibs-elements/exports/Link';
import { Button } from 'dibs-elements/exports/Button';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

// styles
import dibsCss from 'dibs-css';
import styles from './HpSharedTopHeroBannerModuleContentPlacementVariant.module.css';

const messages = defineMessages({
    consumerTopic: {
        id: 'homepage.HpSharedTopHeroBannerModuleContentPlacementVariant.consumer.topic',
        defaultMessage: 'The Latest',
    },
    tradeTopic: {
        id: 'homepage.HpSharedTopHeroBannerModuleContentPlacementVariant.trade.topic',
        defaultMessage: 'The Latest',
    },
    consumerTitle: {
        id: 'homepage.HpSharedTopHeroBannerModuleContentPlacementVariant.consumer.title',
        defaultMessage: 'Editors’ Picks',
    },
    tradeTitle: {
        id: 'homepage.HpSharedTopHeroBannerModuleContentPlacementVariant.trade.title',
        defaultMessage: 'Editors’ Picks',
    },
    consumerDescription: {
        id: 'homepage.HpSharedTopHeroBannerModuleContentPlacementVariant.consumer.description',
        defaultMessage:
            'Our in-house design experts curated a selection of fashion, art, fine jewelry, furniture and more that is not to be missed.',
    },
    tradeDescription: {
        id: 'homepage.HpSharedTopHeroBannerModuleContentPlacementVariant.trade.description',
        defaultMessage:
            'Our in-house design experts curated a selection of fashion, art, fine jewelry, furniture and more that is not to be missed.',
    },
    button: {
        id: 'homepage.HpSharedTopHeroBannerModuleContentPlacementVariant.button',
        defaultMessage: 'Shop Now',
    },
});

export const HpSharedTopHeroBannerModuleContentPlacementVariant: FC<{ isMobile?: boolean }> = ({
    isMobile,
}) => {
    const intl = useIntl();
    const heroBannerRef = useRef(null);
    const { userType } = useHomePageContext();
    const isTrade = userType === TRADE;
    const link = '/collections/editors-picks/';
    const imageUrl =
        'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/top_banner_10-15-25.jpg';

    const topic = intl.formatMessage(messages[`${isTrade ? 'trade' : 'consumer'}Topic`]);
    const title = intl.formatMessage(messages[`${isTrade ? 'trade' : 'consumer'}Title`]);
    const description = intl.formatMessage(
        messages[`${isTrade ? 'trade' : 'consumer'}Description`]
    );

    const promoTrackingObject = buildPromoTrackingObject({
        index: 0,
        id: 'banner-carousel',
        name: 'Banner Carousel',
        item: { linkData: { path: link } },
    });
    return (
        <>
            <VisibilityTracker
                elementRef={heroBannerRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackPromoImpressions([promoTrackingObject]);
                    }
                }}
            />
            <Link
                href={link}
                className={styles.link}
                onClick={() => trackPromoClick(promoTrackingObject)}
            >
                <div
                    ref={heroBannerRef}
                    className={cn(styles.container, dibsCss.wScreen, dibsCss.flex, {
                        [styles.isTrade]: isTrade,
                        [dibsCss.flexCol]: isMobile,
                        [styles.isMobile]: isMobile,
                    })}
                >
                    {isMobile ? (
                        <>
                            <img
                                alt={title}
                                src={imageUrl}
                                sizes="100vw"
                                srcSet={getSrcsetString(imageUrl)}
                                className={cn(styles.image, dibsCss.wFull)}
                            />
                            <div
                                className={cn(
                                    dibsCss.flex,
                                    dibsCss.flexCol,
                                    dibsCss.pxSmall,
                                    dibsCss.pyMedium
                                )}
                            >
                                <span
                                    className={cn(
                                        dibsCss.sassyFontHeaderSmallItalic,
                                        dibsCss.mbXxsmall
                                    )}
                                >
                                    {topic}
                                </span>
                                <span
                                    className={cn(dibsCss.sassyFontHeaderXxLarge, dibsCss.mbSmall)}
                                >
                                    {title}
                                </span>
                                <div>
                                    <Button size="large" fullWidth={false} type="transparent">
                                        {intl.formatMessage(messages.button)}
                                    </Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={dibsCss.w50}>
                                <img
                                    alt={title}
                                    sizes="50vw"
                                    src={imageUrl}
                                    className={styles.image}
                                    srcSet={getSrcsetString(imageUrl, [960, 1280, 1600, 1920])}
                                />
                            </div>
                            <div
                                className={cn(
                                    dibsCss.w50,
                                    dibsCss.flex,
                                    dibsCss.flexCol,
                                    styles.textArea,
                                    dibsCss.justifyCenter
                                )}
                            >
                                <span
                                    className={cn(
                                        dibsCss.mbXsmall,
                                        dibsCss.sassyFontHeaderMediumItalic
                                    )}
                                >
                                    {topic}
                                </span>
                                <span
                                    className={cn(
                                        styles.title,
                                        dibsCss.mbSmall,
                                        dibsCss.sassyFontHeaderSizeXLargeTypeNormal
                                    )}
                                >
                                    {title}
                                </span>
                                <span
                                    className={cn(
                                        dibsCss.mbMedium,
                                        styles.description,
                                        dibsCss.sassyFontHeaderLargeNormal
                                    )}
                                >
                                    {description}
                                </span>
                                <div>
                                    <Button size="large" fullWidth={false} type="transparent">
                                        {intl.formatMessage(messages.button)}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Link>
        </>
    );
};
